/** Editor для Передачі сировини в переробку (Порція 4b). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import SearchSelect from '@/components/SearchSelect';
import {
  materialTransfersForProcessing,
  type MaterialTransferItemIn,
} from '@/api/materialTransfersForProcessing';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.material_transfer.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  processor_contractor_id: string;
  contract_id: string;
  source_warehouse_id: string;
  expected_return_date: string;
  comment: string;
  project_id: string | null;
  items: MaterialTransferItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  processor_contractor_id: '',
  contract_id: '',
  source_warehouse_id: '',
  expected_return_date: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number): MaterialTransferItemIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function MaterialTransferForProcessingEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });
  const [tab, setTab] = useState<'items' | 'accounts'>('items');
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];
  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  // ч.677: сировина додається попапом мультивибору (склад — зі строки попапу).
  const [pickerOpen, setPickerOpen] = useState(false);
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.processor_contractor_id],
    queryFn: () => refsApi.contracts({
      organization_id: form.organization_id,
      contractor_id: form.processor_contractor_id,
    }),
    enabled: !!form.organization_id && !!form.processor_contractor_id,
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['material-transfer-for-processing', id],
    queryFn: () => materialTransfersForProcessing.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.material_transfer_for_processing', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        processor_contractor_id: d.processor_contractor_id,
        contract_id: d.contract_id ?? '',
        source_warehouse_id: d.source_warehouse_id ?? '',
        expected_return_date: d.expected_return_date ? d.expected_return_date.slice(0, 16) : '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          account_code: it.account_code ?? null,
          transfer_account_code: it.transfer_account_code ?? null,
          // ч.677: legacy-док без складу рядка — показуємо склад шапки.
          warehouse_id: it.warehouse_id ?? d.source_warehouse_id ?? null,
          declared_value: it.declared_value ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    processor_contractor_id: form.processor_contractor_id,
    contract_id: form.contract_id || null,
    source_warehouse_id: form.source_warehouse_id || null,
    expected_return_date: form.expected_return_date
      ? new Date(form.expected_return_date).toISOString()
      : null,
    comment: form.comment || null,
        project_id: form.project_id,
    items: form.items,
  });

  const create = useMutation({
    mutationFn: () => materialTransfersForProcessing.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
      navigate(`/material-transfers-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => materialTransfersForProcessing.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-transfer-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await materialTransfersForProcessing.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await materialTransfersForProcessing.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/material-transfers-for-processing/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
      navigate(`/material-transfers-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await materialTransfersForProcessing.update(id!, buildPayload());
      await materialTransfersForProcessing.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-transfer-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => materialTransfersForProcessing.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-transfer-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => materialTransfersForProcessing.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-transfers-for-processing'] });
      navigate('/material-transfers-for-processing');
    },
    onError: (e) => setError(extractError(e)),
  });

  // ч.677: рядки додаються попапом (мультивибір; списання — зі складу рядка).
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((row, k) => ({
        ...newRow(f.items.length + 1 + k),
        nomenclature_id: row.nomenclature_id,
        quantity: '1',
        warehouse_id: row.warehouse_id || null,
      }))],
    }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<MaterialTransferItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  const totalCost = useMemo(
    () =>
      (docQuery.data?.items ?? []).reduce(
        (acc, it) => acc + Number(it.total_cost ?? '0'),
        0,
      ),
    [docQuery.data],
  );

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  const nomName = (nid: string) => {
    const n = nomQ.data?.items.find((x) => x.id === nid);
    return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—';
  };

  // ч.911 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'processor', title: 'Переробник', required: 1,
      filled: form.processor_contractor_id ? 1 : 0 },
    { id: 'items', title: 'Сировина', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова передача в переробку' : `Передача: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.material_transfer_for_processing', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/material-transfers-for-processing')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/material-transfers-for-processing/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.material_transfer_for_processing', id: isNew ? '' : id! }} />
      }>
      {/* ч.677 (розкладка власника): [Юрособа|Номер|Дата] →
          [Переробник|Договір(+створити)|Очікувана дата повернення];
          Склад — у РЯДКАХ ТЧ (0461). ч.911 — блоками. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Переробник">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Переробник *</label>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.processor_contractor_id}
            onChange={(v) => setForm({ ...form, processor_contractor_id: v, contract_id: '' })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            Договір{' '}
            <a href={`/refs/contracts/new${form.processor_contractor_id ? `?contractor_id=${form.processor_contractor_id}` : ''}`}
               target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', fontWeight: 'normal',
               color: 'var(--primary)', marginLeft: 6 }}>створити ↗</a>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.processor_contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Очікувана дата повернення</label>
          <DatePopover withTime value={form.expected_return_date}
                 onChange={(v) => setForm({ ...form, expected_return_date: v })} disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Сировина"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть організацію'
              : 'Підбір сировини з залишків (мультивибір; списання — зі складу рядка)'}>
            + додати номенклатуру
          </button>
        ) : undefined}>
      {/* Таблична частина — вкладки (канон seg-tabs, «Рахунки обліку» справа) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'items' ? 'active' : ''}
            onClick={() => setTab('items')}>Сировина ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
                <th style={{ width: 220 }}>Рах. передачі</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.nomenclature_id).map((it) => {
                const i = form.items.indexOf(it);
                return (
                  <tr key={i}>
                    <td>{nomName(it.nomenclature_id)}</td>
                    <td>{acctSelect(it.account_code, (v) => setItemField(i, { account_code: v }), '201')}</td>
                    <td>{acctSelect(it.transfer_account_code, (v) => setItemField(i, { transfer_account_code: v }), '206')}</td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — рахунок за замовчуванням (вказаний у дужках). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {tab === 'items' && (
        <div style={{ marginTop: '0.75rem' }}>
          {isPosted && (
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
              Вартість: {totalCost.toFixed(2)}
            </div>
          )}
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Сировина</th>
                <th style={{ width: 170 }}>Склад</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 140 }}
                    title="Договірна оцінка для переробника — база ЙОГО матвідповідальності і прибуткування на 022; НЕ ваша собівартість, на проводки не впливає, друкується в акті">
                  Задекл. вартість</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.line_number}</td>
                  <td>
                    <NomenclatureSelect
                      value={it.nomenclature_id}
                      disabled={isPosted}
                      onChange={(v) => setItemField(i, { nomenclature_id: v })}
                      kind="goods"
                    />
                  </td>
                  <td>
                    <select value={it.warehouse_id ?? ''}
                            onChange={(e) => setItemField(i, { warehouse_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">— оберіть склад —</option>
                      {warehousesQ.data?.map((w) => (
                        <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.001" value={it.quantity}
                           onChange={(e) => setItemField(i, { quantity: e.target.value })}
                           disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={it.declared_value ?? ''}
                           onChange={(e) => setItemField(i, { declared_value: e.target.value || null })}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>
                    {!isPosted && <button onClick={() => removeItem(i)} title="Видалити">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {/* ч.677: попап підбору сировини — канон комплектації (мультивибір,
          розворот по складах, лише додатний залишок за замовчуванням). */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          expandByWarehouse
          defaultOnlyWithStock
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.material_transfer_for_processing" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
